import * as React from 'react';
import { clsx } from 'clsx';
import { SectionIcon, SectionIconVariant } from '../SectionIcon';

export interface ModalHeaderProps {
  /**
   * The title text for the modal header
   */
  title: string;
  /**
   * Optional icon variant to display before the title
   * Uses the SectionIcon component variants: 'warning', 'danger', 'info', 'success'
   */
  icon?: SectionIconVariant;
  /**
   * Callback when close button is clicked
   * If not provided, close button will not be shown
   */
  onClose?: () => void;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

// Close X icon
const CloseIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1 1L9 9M9 1L1 9"
      stroke="#2f2f2f"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Arbor Design System modal header styles
const modalHeaderStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '16px',
    backgroundColor: '#ffffff',
    boxSizing: 'border-box' as const,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    minHeight: '56px',
  },
  leftContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1,
  },
  title: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#2f2f2f',
    lineHeight: '1.25',
    margin: 0,
  },
  closeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '16px',
    backgroundColor: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    flexShrink: 0,
    transition: 'background-color 0.15s ease-in-out',
  },
  closeButtonHover: {
    backgroundColor: '#f8f8f8',
  },
  closeButtonFocus: {
    outline: '3px solid #3cad51',
    outlineOffset: '-3px',
  },
};

/**
 * ModalHeader component - Arbor Design System
 *
 * The header part of a modal dialog with title, optional icon, and close button.
 * The icon uses SectionIcon variants (warning, danger, info, success).
 *
 * @example
 * ```tsx
 * // Basic modal header with close button
 * <ModalHeader
 *   title="What would you like to do?"
 *   onClose={() => setIsOpen(false)}
 * />
 *
 * // Modal header with warning icon
 * <ModalHeader
 *   title="Are you sure?"
 *   icon="warning"
 *   onClose={() => setIsOpen(false)}
 * />
 *
 * // Modal header with danger icon (error state)
 * <ModalHeader
 *   title="Sorry, we can't find this page"
 *   icon="danger"
 *   onClose={() => setIsOpen(false)}
 * />
 *
 * // Modal header without close button
 * <ModalHeader
 *   title="Processing..."
 *   icon="info"
 * />
 * ```
 */
export const ModalHeader = React.forwardRef<HTMLDivElement, ModalHeaderProps>(
  ({ title, icon, onClose, className, style, ...props }, ref) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);

    const closeButtonStyle: React.CSSProperties = {
      ...modalHeaderStyles.closeButton,
      ...(isHovered && modalHeaderStyles.closeButtonHover),
      ...(isFocused && modalHeaderStyles.closeButtonFocus),
    };

    return (
      <div
        ref={ref}
        className={clsx('arbor-modal-header', className)}
        style={{ ...modalHeaderStyles.container, ...style }}
        {...props}
      >
        <div style={modalHeaderStyles.leftContent}>
          {icon && <SectionIcon variant={icon} />}
          <h2 style={modalHeaderStyles.title}>{title}</h2>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            style={closeButtonStyle}
            aria-label="Close modal"
          >
            <CloseIcon />
          </button>
        )}
      </div>
    );
  }
);

ModalHeader.displayName = 'ModalHeader';
